import { useApolloClient, useMutation } from 'react-apollo';
import produce from 'immer';

import {
  ALBUM_PHOTO_FILTERS,
  ALBUM_PHOTO_STATUS,
} from '../../../../../../../../../../../constants/albums';
import {
  albumPhotosDelete as albumPhotosDeleteMutation,
  albumPhotosPublish as albumPhotosPublishMutation,
  albumPhotosHide as albumPhotosHideMutation,
} from '../../../../../../../../../../../graphql/mutations/albums';
import {
  AlbumDeletedPhotos,
  AlbumPhotosWithPhotoGroups,
} from '../../../../../../../../../../../graphql/queries/albums';
import { updateStoreAfterReview } from '../../../../../../../../../../../util/album-folder-review';
import {
  ALBUM_PHOTO_GROUPS_SORT,
  SORT_DIRECTION,
} from '../../../../../../../../../../../constants/sort';

const useUngroupedPhotoActions = ({ album }) => {
  const client = useApolloClient();
  const [albumPhotosDelete] = useMutation(albumPhotosDeleteMutation);
  const [albumPhotosPublish] = useMutation(albumPhotosPublishMutation);
  const [albumPhotosHide] = useMutation(albumPhotosHideMutation);

  const onAlbumPhotosDelete = async ({
    albumId,
    photoIds,
    albumPhotoFilter,
    uploaderIds,
    sortByShootTime,
  }) => {
    await albumPhotosDelete({
      variables: {
        input: {
          album: albumId,
          photo_ids: photoIds,
          album_photo_filter: albumPhotoFilter,
          uploader_ids: uploaderIds,
          album_photos_delete_mode: 'ALBUM_PHOTOS_ONLY', // From here, We don't touch deleted photos.
        },
      },
      refetchQueries: (() => {
        const albumQuery = {
          query: AlbumPhotosWithPhotoGroups,
          variables: {
            id: albumId,
            ignore_watermark: true,
            limit: 100,
            offset: 0,
            album_photo_filter: albumPhotoFilter || ALBUM_PHOTO_FILTERS.ALL,
            uploader_ids: uploaderIds || [],
            sort_by_shoot_time: sortByShootTime || false,
          },
        };

        const deletedPhotos = {
          query: AlbumDeletedPhotos,
          variables: {
            id: albumId,
            limit: 100,
            offset: 0,
          },
        };

        return [albumQuery, deletedPhotos];
      })(),
    });
  };

  const onAlbumPhotosPublish = async ({
    albumId,
    photoIds,
    albumPhotoFilter,
    uploaderIds,
    sortByShootTime,
  }) => {
    await albumPhotosPublish({
      variables: {
        input: {
          album: albumId,
          photo_ids: photoIds,
          album_photo_filter: albumPhotoFilter,
          uploader_ids: uploaderIds,
          album_photos_publish_mode: 'HIDDEN_ONLY', // From here, We don't touch deleted photos.
        },
        albumId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        albumPhotosPublish: {
          __typename: 'AlbumPhotosPublishPayload',
          photos: (photoIds.selected_ids || []).map(id => ({
            id,
            album_photo_status: ALBUM_PHOTO_STATUS.PUBLISHED,
            thumbnail_urls: [],
            __typename: 'Photo',
          })),
        },
      },
      update: (
        store,
        {
          data: {
            albumPhotosPublish: { photos: updatedPhotoItems },
          },
        },
      ) => {
        const isSelectAll = photoIds?.select_all;
        const ignoredIds = photoIds?.ignored_ids || [];
        let updatedPhotosTotal = 0;

        if (albumPhotoFilter === ALBUM_PHOTO_FILTERS.ALL) {
          return;
        }

        const updatedPhotoItemsIds = updatedPhotoItems.map(({ id }) => id);
        const variables = {
          id: albumId,
          ignore_watermark: true,
          limit: 100,
          offset: 0,
          album_photo_filter: albumPhotoFilter || ALBUM_PHOTO_FILTERS.ALL,
          uploader_ids: uploaderIds || [],
          sort: {
            field: ALBUM_PHOTO_GROUPS_SORT.CREATED_AT,
            direction: SORT_DIRECTION.ASC,
          },
          sort_by_shoot_time: sortByShootTime || false,
        };

        const data = store.readQuery({
          query: AlbumPhotosWithPhotoGroups,
          variables,
        });

        const updatedData = produce(data, draft => {
          updatedPhotosTotal = isSelectAll
            ? draft.album.photos_ungrouped.total + ignoredIds.length
            : updatedPhotoItemsIds.length;

          draft.album.photos_ungrouped.total -= updatedPhotosTotal;
          draft.album.photos_ungrouped.count -= isSelectAll
            ? draft.album.photos_ungrouped.count + ignoredIds.length
            : updatedPhotoItemsIds.length;
          draft.album.photos_ungrouped.records = isSelectAll
            ? data.album.photos_ungrouped.records.filter(({ id }) =>
                ignoredIds.includes(id),
              )
            : data.album.photos_ungrouped.records.filter(
                ({ id }) => !updatedPhotoItemsIds.includes(id),
              );

          if (albumPhotoFilter === ALBUM_PHOTO_FILTERS.REPORT_REVIEW) {
            // ensure we alter the `reported_photos` total
            draft.album.reported_photos.total -= updatedPhotosTotal;
          }

          if (albumPhotoFilter === ALBUM_PHOTO_FILTERS.DNP) {
            // ensure we alter the `dnp_photos` total
            draft.album.dnp_photos.total -= updatedPhotosTotal;
          }
        });

        store.writeQuery({
          query: AlbumPhotosWithPhotoGroups,
          variables,
          data: updatedData,
        });

        // IMPORTANT: This breaks under 1 condition: If you go into the "All" tab, select all & publish all photos.
        // The reason for this, is that since we don't get specific photo items when selecting all, we will not know which
        // photos were under review, meaning that we will have no idea which ones were updated.
        // At the time of writing, we don't support that, and probably never will, but thought it was important to specify this note
        // in case someone attempted to enable this.
        const photosUnderReviewTotal =
          isSelectAll && albumPhotoFilter === ALBUM_PHOTO_STATUS.REVIEWING
            ? updatedPhotosTotal
            : updatedPhotoItems.filter(
                photo =>
                  photo.album_photo_status === ALBUM_PHOTO_STATUS.REVIEWING,
              ).length;

        if (photosUnderReviewTotal > 0 && album) {
          // update fragments accordingly:
          updateStoreAfterReview(album, store, photosUnderReviewTotal);
        }
      },
      refetchQueries: (() => {
        const refetchQueries = [];
        // We don't need to refetch filter we're currently in
        const filtersToCheck = Object.keys(ALBUM_PHOTO_FILTERS).filter(
          filter => filter !== albumPhotoFilter,
        );

        filtersToCheck.forEach(filter => {
          const baseVariables = {
            ignore_watermark: true,
            limit: 100,
            offset: 0,
            album_photo_filter: filter,
            uploader_ids: uploaderIds || [],
            sort: {
              field: ALBUM_PHOTO_GROUPS_SORT.CREATED_AT,
              direction: SORT_DIRECTION.ASC,
            },
            sort_by_shoot_time: sortByShootTime || false,
          };

          try {
            client.readQuery({
              query: AlbumPhotosWithPhotoGroups,
              variables: {
                id: albumId,
                ...baseVariables,
                limit: 100,
              },
            });

            refetchQueries.push({
              query: AlbumPhotosWithPhotoGroups,
              variables: {
                id: albumId,
                ...baseVariables,
                limit: 100,
              },
            });
          } catch (error) {
            // Nothing to do. Just won't add it to the refetchQuery array
            // Error will be triggered by apollo any time readQuery is a
            // query not available in the store.
          }
        });
        return refetchQueries;
      })(),
    });
  };

  const onAlbumPhotosHide = async ({
    albumId,
    photoIds,
    albumPhotoFilter,
    uploaderIds,
    sortByShootTime,
  }) => {
    await albumPhotosHide({
      variables: {
        input: {
          album: albumId,
          photo_ids: photoIds,
          album_photo_filter: albumPhotoFilter,
          uploader_ids: uploaderIds,
        },
        albumId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        albumPhotosHide: {
          __typename: 'AlbumPhotosHidePayload',
          photos: (photoIds.selected_ids || []).map(id => ({
            id,
            album_photo_status: ALBUM_PHOTO_STATUS.UNPUBLISHED,
            thumbnail_urls: [],
            __typename: 'Photo',
          })),
        },
      },
      update: (
        store,
        {
          data: {
            albumPhotosHide: { photos: hiddenPhotos },
          },
        },
      ) => {
        if (albumPhotoFilter === ALBUM_PHOTO_FILTERS.ALL) {
          return;
        }

        const hiddenPhotosId = hiddenPhotos.map(({ id }) => id);
        const isSelectAll = photoIds?.select_all;
        const ignoredIds = photoIds?.ignored_ids || [];
        const variables = {
          id: albumId,
          ignore_watermark: true,
          limit: 100,
          offset: 0,
          album_photo_filter: albumPhotoFilter || ALBUM_PHOTO_FILTERS.ALL,
          uploader_ids: uploaderIds || [],
          sort: {
            field: ALBUM_PHOTO_GROUPS_SORT.CREATED_AT,
            direction: SORT_DIRECTION.ASC,
          },
          sort_by_shoot_time: sortByShootTime || false,
        };

        const data = store.readQuery({
          query: AlbumPhotosWithPhotoGroups,
          variables,
        });

        const updatedData = produce(data, draft => {
          draft.album.photos_ungrouped.total -= isSelectAll
            ? draft.album.photos_ungrouped.total + ignoredIds.length
            : hiddenPhotosId.length;
          draft.album.photos_ungrouped.count -= isSelectAll
            ? draft.album.photos_ungrouped.count + ignoredIds.length
            : hiddenPhotosId.length;
          draft.album.photos_ungrouped.records = isSelectAll
            ? data.album.photos_ungrouped.records.filter(({ id }) =>
                ignoredIds.includes(id),
              )
            : data.album.photos_ungrouped.records.filter(
                ({ id }) => !hiddenPhotosId.includes(id),
              );
        });

        store.writeQuery({
          query: AlbumPhotosWithPhotoGroups,
          variables,
          data: updatedData,
        });
      },
      refetchQueries: (() => {
        const refetchQueries = [];

        // We don't need to refetch filter we're currently in
        const filtersToCheck = Object.keys(ALBUM_PHOTO_FILTERS).filter(
          filter => filter !== albumPhotoFilter,
        );

        filtersToCheck.forEach(filter => {
          const baseVariables = {
            ignore_watermark: true,
            limit: 100,
            offset: 0,
            album_photo_filter: filter,
            uploader_ids: uploaderIds || [],
            sort: {
              field: ALBUM_PHOTO_GROUPS_SORT.CREATED_AT,
              direction: SORT_DIRECTION.ASC,
            },
            sort_by_shoot_time: sortByShootTime || false,
          };

          try {
            client.readQuery({
              query: AlbumPhotosWithPhotoGroups,
              variables: {
                id: albumId,
                ...baseVariables,
              },
            });

            refetchQueries.push({
              query: AlbumPhotosWithPhotoGroups,
              variables: {
                id: albumId,
                ...baseVariables,
              },
            });
          } catch (error) {
            // Nothing to do. Just won't add it to the refetchQuery array
            // Error will be triggered by apollo any time readQuery is a
            // query not available in the store.
          }
        });
        return refetchQueries;
      })(),
    });
  };

  return {
    onAlbumPhotosDelete,
    onAlbumPhotosPublish,
    onAlbumPhotosHide,
  };
};

export default useUngroupedPhotoActions;
